import {useFieldArray, useForm} from "react-hook-form";
import { useTranslation } from "react-i18next";
import Button from "Components/Button";
import Space from "Components/Space";
import FlexRow from "Components/FlexRow";
import { useEffect } from "react";
import ControlledSelect from "../../Components/Select/ControlledSelect";
import Select from "../../Components/Select";
import useFilteredList from "../../Hooks/UseFilteredList";
import {ProjectSettlementSettingsDto} from "../../Apis/Models/ProjectSettlementSettingsDto";
import {useProjects} from "../../Apis/ProjectApi";
import {useMutateProjectSettlementSettings} from "../../Apis/ProjectSettlementSettingsApi";
import ControlledInput from "../../Components/Input";
import React from "react";
import {FeeReceiverSelect} from "./FeeReceiverSelect";
import XIcon from "../../Assets/SVG/XIcon";
import FormFieldBox from "../../Components/FormFieldBox";

interface IProps {
    settlementSettings?: ProjectSettlementSettingsDto;
    onSubmit?: (data: ProjectSettlementSettingsDto) => void;
}
const ProjectSettlementSettingsForm = (props: IProps) => {

    const { t } = useTranslation();

    const { control, handleSubmit, reset, watch } = useForm<ProjectSettlementSettingsDto>();
    const { fields: packagingField, append: addPackagingFee, remove: removePackagingFee } = useFieldArray({ control, name: "packagingFees"})
    const { fields: paymentFields, append: addPaymentFee, remove: removePaymentFee } = useFieldArray({ control, name: "paymentFees"})
    const { fields: b2bFields, append: addB2BField, remove: removeB2BField } = useFieldArray({ control, name: "b2BFees"})
    const { fields: splitFields, append: addSplit, remove: removeSplit } = useFieldArray({ control, name: "splits"})
    const { fields: recurringFields, append: addRecurring, remove: removeRecurring } = useFieldArray({ control, name: "recurringFees"})
    
    const { data: projects } = useProjects({includeAll: true});

    const { mutateAsync, isLoading } = useMutateProjectSettlementSettings();

    const [ filteredProjects, setFilteredProjects ] = useFilteredList(projects?.items ?? [], x => [x.name]);
    const [ filteredRecurringIntervals, setFilteredRecurringIntervals  ] = useFilteredList([{ value: 'Monthly', label: t('monthly')}], x => [x.label]);

    useEffect(() => {
        if (props.settlementSettings) {
            reset(props.settlementSettings);
        }
    }, [reset, props.settlementSettings])

    const onSubmit = async(data: ProjectSettlementSettingsDto) => {
        await mutateAsync({ ...data});

        if (props.onSubmit) {
            props.onSubmit(data);
        }

        reset();
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Space direction={"vertical"} size={"small"}>
                <ControlledSelect control={control} placeholder={t('project')} name={"projectId"} onFilter={setFilteredProjects}>
                    {filteredProjects?.map(x => <Select.Option key={x.id} label={x.name} value={x.id} />)}
                </ControlledSelect>

                <ControlledInput name={`currency`} type={"string"} control={control} placeholder={t('currency')} defaultValue={"DKK"} />
                
                <h2>
                    {t('packgingFees')}
                </h2>
                
                {packagingField.map((field, index) => (
                    <FormFieldBox key={field.id}>
                        <div className={"deletePositionAbsolute"} onClick={() => removePackagingFee(index)}>
                            <XIcon />
                        </div>
                        <FeeReceiverSelect control={control} name={`packagingFees.${index}.name`}></FeeReceiverSelect>
                        <ControlledInput name={`packagingFees.${index}.amount`} type={"number"} control={control} placeholder={t('value')} suffix={watch('currency')} />
                    </FormFieldBox>
                ))}

                <Button buttonType={"button"} type={"secondary"} text={t('addFee')} onClick={() => addPackagingFee({name: '', amount: 0})} />
                
                <h2>
                    {t('paymentFees')}
                </h2>
                
                {paymentFields.map((field, index) => (
                    <FormFieldBox key={field.id}>
                        <div className={"deletePositionAbsolute"} onClick={() => removePaymentFee(index)}>
                            <XIcon />
                        </div>
                        <FeeReceiverSelect control={control} name={`paymentFees.${index}.name`}></FeeReceiverSelect>
                        <ControlledInput name={`paymentFees.${index}.amount`} type={"number"} control={control} placeholder={t('value')} suffix={"%"} />
                    </FormFieldBox>
                ))}

                <Button buttonType={"button"} type={"secondary"} text={t('addFee')} onClick={() => addPaymentFee({name: '', amount: 0})} />

                <h2>
                    {t('b2bFees')}
                </h2>

                {b2bFields.map((field, index) => (
                    <FormFieldBox key={field.id}>
                        <div className={"deletePositionAbsolute"} onClick={() => removeB2BField(index)}>
                            <XIcon />
                        </div>
                        <FeeReceiverSelect control={control} name={`b2BFees.${index}.name`}></FeeReceiverSelect>
                        <ControlledInput name={`b2BFees.${index}.amount`} type={"number"} control={control} placeholder={t('value')} suffix={"%"} />
                    </FormFieldBox>
                ))}

                <Button buttonType={"button"} type={"secondary"} text={t('addFee')} onClick={() => addB2BField({name: '', amount: 0})} />
                
                <h2>
                    {t('splits')}
                </h2>

                {splitFields.map((field, index) => (
                    <FormFieldBox key={field.id}>
                        <div className={"deletePositionAbsolute"} onClick={() => removeSplit(index)}>
                            <XIcon />
                        </div>
                        <FeeReceiverSelect control={control} name={`splits.${index}.name`}></FeeReceiverSelect>
                        <ControlledInput name={`splits.${index}.amount`} type={"number"} control={control} placeholder={t('value')} suffix={"%"} />
                    </FormFieldBox>
                ))}

                <Button buttonType={"button"} type={"secondary"} text={t('addFee')} onClick={() => addSplit({name: '', amount: 0})} />

                <h2>
                    {t('recurringFees')}
                </h2>

                {recurringFields.map((field, index) => (
                    <FormFieldBox key={field.id}>
                        <div className={"deletePositionAbsolute"} onClick={() => removeRecurring(index)}>
                            <XIcon />
                        </div>
                        
                        <ControlledInput name={`recurringFees.${index}.feeName`} control={control} placeholder={t('name')} />
                        
                        <ControlledSelect onFilter={setFilteredRecurringIntervals} control={control} placeholder={t('recurringInterval')} name={`recurringFees.${index}.recurringInterval`}>
                            {filteredRecurringIntervals.map(option => <Select.Option value={option.value} label={option.label} />)}
                        </ControlledSelect>
                        
                        <RecurringFeeSplit fieldName={`recurringFees.${index}`} control={control} currency={watch('currency')} />
                    </FormFieldBox>
                ))}

                <Button buttonType={"button"} type={"secondary"} text={t('addRecurringFee')} onClick={() => addRecurring({name: '', amount: 0, recurringInterval: 'Monthly', feeName: '', recurringLastProcessed: undefined})} />

                
                <FlexRow justify={"end"}>
                    <Button buttonType={"submit"} type={"primary"} text={t('save')} loading={isLoading} />
                </FlexRow>
            </Space>
        </form>
    )
}

type Props = {
    control: any,
    fieldName: string,
    currency: string;
}
const RecurringFeeSplit = ({ control, fieldName, currency}: Props) => {
    
    const { t } = useTranslation()
    
    const { fields: splits, append: addSplit, remove: removeSplit } = useFieldArray({ control, name: `${fieldName}.splits`})
    
    return (
        <Space>

            {splits.map((field, index) => (
                <div style={{position: 'relative'}} key={field.id}>
                    <hr/>
                    <div className={"deletePositionAbsolute"} onClick={() => removeSplit(index)}>
                        <XIcon />
                    </div>
                    <FeeReceiverSelect control={control} name={`${fieldName}.splits.${index}.name`}></FeeReceiverSelect>
                    <ControlledInput name={`${fieldName}.splits.${index}.amount`} type={"number"} control={control} placeholder={t('value')} suffix={currency} />
                </div>
            ))}

            <Button buttonType={"button"} type={"secondary"} text={t('addSplit')} onClick={() => addSplit({name: '', amount: 0})} />
        </Space>
    )
}

export default ProjectSettlementSettingsForm
